import is from '@sindresorhus/is';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext, useRef } from 'react';
import Maybe from 'true-myth/maybe';

import { useJobFilter } from './use-job-filter';
import { ApplicationContext } from '../../../../shared/source/context/context';
import { exportFileSchema } from '../../../../shared/source/data-contract/common';
import { mapToAttributesWithValue } from '../../../../shared/source/data-contract/custom-attribute';
import {
    jobCandidatePaginatedSchema,
    jobCandidateSchema,
    jobGenerationResponseSchema,
    jobPaginatedSchema,
    jobSchema,
    jobShortListCandidateArraySchema,
    jobsOptionsSchema,
    singleJobOptionsSchema,
} from '../../../../shared/source/data-contract/job';
import { paginationCountResponseSchema } from '../../../../shared/source/data-contract/pagination';

import type { ExportFile, PagedBodyParams, SortedBodyParams } from '../../../../shared/source/data-contract/common';
import type {
    CustomAttribute,
    CustomAttributes,
    CustomAttributeWithValue,
} from '../../../../shared/source/data-contract/custom-attribute';
import type { GeneralFilter } from '../../../../shared/source/data-contract/filter';
import type { JobMutation, JobPatchMutation } from '../../../../shared/source/data-contract/job';
import type {
    Job,
    JobCandidate,
    JobGenerateDescriptionRequest,
    JobGenerationResponse,
    JobPaginated,
    JobShortListCandidate,
    JobsOptions,
    JobState,
    SingleJobOptions,
} from '../../../../shared/source/data-contract/job';
import type { PaginatedResponse, PaginationCountResponse } from '../../../../shared/source/data-contract/pagination';
import type { User } from '../../../../shared/source/data-contract/user';
import type { HttpFetcherError, Pathname } from '../../../../shared/source/http-fetcher/http-fetcher';
import type { LanguageCode } from '../../../../shared/source/internationalization';
import type {
    InfiniteData,
    QueryKey,
    UseInfiniteQueryResult,
    UseMutationResult,
    UseQueryResult,
} from '@tanstack/react-query';
import type Result from 'true-myth/result';

const candidatesQueryKeyPrefix = 'candidates';

const recommendationFilterStaleTimeMs = 60_000;
const candidatesRefetchIntervalMilliseconds = 5000;
const candidatesRefetchMaxJobAge = 300_000;

export const useJobsOptionsQuery = (): UseQueryResult<JobsOptions, HttpFetcherError> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const pathname: Pathname = `/jobs/options`;

    return useQuery({
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(jobsOptionsSchema, {
                pathname,
            });

            if (result.isOk) {
                return result.value;
            }

            return result.error as any;
        },
        queryKey: [pathname, 'GET'],
    });
};

type UseJobsPermissionsReturnType = {
    readonly canUserCreateJobs: boolean;
    readonly canUserManageJobs: boolean;
};

export const useJobsPermissions = (): UseJobsPermissionsReturnType => {
    const { data: jobsOptions } = useJobsOptionsQuery();

    const canUserManageJobs = jobsOptions?.canManageJobs ?? false;
    const canUserCreateJobs = jobsOptions?.canCreateJobs ?? false;

    return {
        canUserCreateJobs,
        canUserManageJobs,
    };
};

export const useJobsFilterQuery = ({
    enabled = true,
    filter,
    paged = { page: 0, size: 20 },
    sorted,
}: {
    readonly enabled: boolean;
    readonly filter?: GeneralFilter;
    readonly paged?: PagedBodyParams;
    readonly sorted?: SortedBodyParams;
}): UseQueryResult<JobPaginated> => {
    const {
        applicationState: { language },
        validatingCobrainerApiFetcher,
    } = useContext(ApplicationContext);

    return useQuery({
        enabled,
        placeholderData: keepPreviousData,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.postJson(jobPaginatedSchema, {
                pathname: '/jobs/filter',
                payload: {
                    filter,
                    paged,
                    sorted,
                },
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: ['/jobs/filter', 'postJson', language.code, filter, paged, sorted],
    });
};

export const useJobOptionsQuery = (jobId: string): UseQueryResult<SingleJobOptions, HttpFetcherError> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const pathname: Pathname = `/jobs/${jobId}/options`;

    return useQuery({
        enabled: is.nonEmptyString(jobId),
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(singleJobOptionsSchema, {
                pathname,
            });

            if (result.isOk) {
                return result.value;
            }

            return result.error as any;
        },
        queryKey: [pathname, 'GET'],
    });
};

type JobPermissions = {
    readonly canUserDeleteJob: boolean;
    readonly canUserEditJob: boolean;
    readonly canUserGetCandidate: boolean;
    readonly canUserGetCandidates: boolean;
    readonly canUserViewJob: boolean;
    readonly isPending: boolean;
};

export const useJobPermissions = (jobId: string): JobPermissions => {
    const { data: jobOptions, isPending } = useJobOptionsQuery(jobId);

    const canUserViewJob = jobOptions?.canReadJob ?? false;
    const canUserEditJob = jobOptions?.canEditJob ?? false;
    const canUserDeleteJob = jobOptions?.canDeleteJob ?? false;
    const canUserGetCandidates = jobOptions?.canReadCandidates ?? false;
    const canUserGetCandidate = jobOptions?.canReadCandidates ?? false;

    return {
        canUserDeleteJob,
        canUserEditJob,
        canUserGetCandidate,
        canUserGetCandidates,
        canUserViewJob,
        isPending,
    };
};

type ShortListPermissions = {
    readonly canUserAddToShortList: boolean;
    readonly canUserExportShortlist: boolean;
    readonly canUserGetShortList: boolean;
    readonly canUserRemoveFromShortList: boolean;
    readonly canUserReorderShortlist: boolean;
    readonly maxShortListLength: number;
};

export const useShortListPermissions = (jobId: string): ShortListPermissions => {
    const { data: jobOptions } = useJobOptionsQuery(jobId);

    const canUserGetShortList = jobOptions?.canReadShortlist ?? false;
    const canUserAddToShortList = jobOptions?.canEditShortlist ?? false;
    const canUserRemoveFromShortList = jobOptions?.canEditShortlist ?? false;
    const canUserReorderShortlist = jobOptions?.canEditShortlist ?? false;
    const canUserExportShortlist = jobOptions?.canExportShortlist ?? false;
    const maxShortListLength = 50;

    return {
        canUserAddToShortList,
        canUserExportShortlist,
        canUserGetShortList,
        canUserRemoveFromShortList,
        canUserReorderShortlist,
        maxShortListLength,
    };
};

export const useJobQuery = (
    jobId: string,
): {
    readonly invalidateJobQuery: () => Promise<void>;
    readonly jobQuery: UseQueryResult<Job, unknown>;
} => {
    const { applicationState, validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const { language } = applicationState;
    const pathname: Pathname = `/jobs/${jobId}`;
    const queryClient = useQueryClient();

    const queryKey = [pathname, 'GET', language.code];

    const jobQuery: UseQueryResult<Job, unknown> = useQuery({
        enabled: is.nonEmptyString(jobId),
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(jobSchema, {
                pathname,
            });
            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey,
    });

    const invalidateJobQuery = (): Promise<void> => queryClient.invalidateQueries({ queryKey });

    return {
        invalidateJobQuery,
        jobQuery,
    };
};

type JobMutations = {
    readonly jobDeleteMutation: UseMutationResult<unknown, Error, string, unknown>;
    readonly jobPatchMutation: UseMutationResult<Job, Error, JobPatchMutation, unknown>;
    readonly jobPostMutation: UseMutationResult<Job, Error, JobMutation, unknown>;
    readonly jobViewedMutation: UseMutationResult<unknown, Error, string>;
};

export const useJobMutations = (): JobMutations => {
    const { cobrainerApiFetcher, validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const queryClient = useQueryClient();
    const pathname: Pathname = `/jobs`;

    const invalidateCache = (jobId?: string): void => {
        if (is.string(jobId)) {
            queryClient.invalidateQueries({ queryKey: [`${pathname}/${jobId}`] });
        }
        queryClient.invalidateQueries({ queryKey: [pathname] });
    };

    const jobPostMutation = useMutation({
        mutationFn: async (data: JobMutation) => {
            const result = await validatingCobrainerApiFetcher.postJson(jobSchema, {
                pathname,
                payload: data,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        onSuccess: () => invalidateCache(),
    });

    const jobPatchMutation = useMutation({
        mutationFn: async (data: JobPatchMutation) => {
            const result = await validatingCobrainerApiFetcher.patchJson(jobSchema, {
                pathname: `${pathname}/${data.id}`,
                payload: data,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        onSuccess: (_, jobData) => invalidateCache(jobData.id),
    });

    const jobDeleteMutation = useMutation({
        mutationFn: async (jobId: string) => {
            const result = await cobrainerApiFetcher.delete({
                pathname: `${pathname}/${jobId}`,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        onSuccess: (_, jobId) => invalidateCache(jobId),
    });

    const jobViewedMutation = useMutation({
        mutationFn: async (jobId: string) => {
            const result = await cobrainerApiFetcher.post({
                pathname: `${pathname}/${jobId}/viewed`,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        onSuccess: (_, jobId) => invalidateCache(jobId),
    });

    return {
        jobDeleteMutation,
        jobPatchMutation,
        jobPostMutation,
        jobViewedMutation,
    };
};

export const useJobsCustomAttributes = (): readonly CustomAttribute[] => {
    const { data: jobsOptions } = useJobsOptionsQuery();

    return mapToAttributesWithValue(Maybe.of(jobsOptions?.customAttributes), Maybe.nothing<CustomAttributes>());
};

export const useJobCustomAttributes = (jobId: string): readonly CustomAttributeWithValue[] => {
    const { data: jobsOptions } = useJobsOptionsQuery();
    const { jobQuery } = useJobQuery(jobId);

    return mapToAttributesWithValue(Maybe.of(jobsOptions?.customAttributes), Maybe.of(jobQuery.data?.custom));
};

export const calculateJobCandidateRecommendationsRefetchInterval = (
    data: InfiniteData<PaginatedResponse<JobCandidate>> | undefined,
    error: Error | null,
    jobHasNoSkills: boolean,
    jobUpdatedAt: string,
): number | false => {
    const recentlyChanged = Date.now() - Date.parse(jobUpdatedAt) < candidatesRefetchMaxJobAge;
    const hasCandidates = is.nonEmptyArray(data?.pages?.flatMap((page) => page.data) ?? []);
    if (jobHasNoSkills || is.null(error) === false || !recentlyChanged || hasCandidates) {
        return false;
    }

    return candidatesRefetchIntervalMilliseconds;
};

export const useJobCandidateRecommendationsQuery = (
    jobId: string,
    jobHasNoSkills: boolean,
    jobUpdatedAt: string,
    filter?: GeneralFilter,
): {
    readonly candidates: readonly JobCandidate[];
    readonly jobCandidatesQuery: UseInfiniteQueryResult;
    readonly nextPageSize: number;
    readonly totalNumberOfCandidates: number;
} => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const fetchCandidatesPageSize = 20;
    const pathname: Pathname = `/jobs/${jobId}/candidates/filter`;

    const jobCandidatesQuery = useInfiniteQuery<PaginatedResponse<JobCandidate>>({
        enabled: is.nonEmptyString(jobId),
        getNextPageParam: (lastPage) => {
            const { page, pages } = lastPage.pagination;
            const nextPage = page + 1;

            return nextPage < pages ? nextPage : null;
        },
        initialPageParam: 0,
        placeholderData: keepPreviousData,
        queryFn: async ({ pageParam }) => {
            const result = await validatingCobrainerApiFetcher.postJson(jobCandidatePaginatedSchema, {
                pathname,
                payload: {
                    filter,
                    paged: {
                        page: pageParam,
                        size: fetchCandidatesPageSize,
                    },
                },
            });
            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [candidatesQueryKeyPrefix, pathname, 'POST', filter, fetchCandidatesPageSize],
        refetchInterval: (query) =>
            calculateJobCandidateRecommendationsRefetchInterval(
                query.state.data,
                query.state.error,
                jobHasNoSkills,
                jobUpdatedAt,
            ),
        staleTime: recommendationFilterStaleTimeMs,
    });

    const candidates = jobCandidatesQuery.data?.pages.flatMap((page) => page.data) ?? [];

    const lastPage = jobCandidatesQuery.data?.pages.slice(-1)[0];
    const totalNumberOfCandidates = lastPage?.pagination?.count ?? 0;
    const numberOfLoadedCandidates = candidates.length;

    const nextPageSize = Math.min(totalNumberOfCandidates - numberOfLoadedCandidates, fetchCandidatesPageSize);

    return {
        candidates,
        jobCandidatesQuery,
        nextPageSize,
        totalNumberOfCandidates,
    };
};

const jobCandidateUrl = (jobId: string, userId: string): Pathname => `/jobs/${jobId}/candidates/${userId}`;

export const useRelatedToJobCandidateQuery = ({
    jobId,
    userId,
}: {
    readonly jobId: string;
    readonly userId: string;
}): UseQueryResult<JobCandidate> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    const pathname: Pathname = jobCandidateUrl(jobId, userId);

    return useQuery({
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(jobCandidateSchema, {
                pathname,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },

        queryKey: [pathname, 'GET'],
    });
};

const getCandidateQueryKey = (jobId: string, candidateId: string): QueryKey => [jobCandidateUrl(jobId, candidateId)];

type SeedCandidateQueryCache = {
    readonly seedCandidateCache: (candidate: JobCandidate) => void;
};

export const useSeedCandidateQueryCache = (jobId: string): SeedCandidateQueryCache => {
    const queryClient = useQueryClient();

    const seedCandidateCache = (candidate: JobCandidate): void => {
        const candidateQueryKey = getCandidateQueryKey(jobId, candidate.id);
        queryClient.setQueryData(candidateQueryKey, candidate);
        queryClient.invalidateQueries({ queryKey: candidateQueryKey });
    };

    return { seedCandidateCache };
};

type JobShortlistQuery = UseQueryResult<readonly JobShortListCandidate[]>;

export const useJobShortListQuery = (jobId: string): JobShortlistQuery => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    const pathname: Pathname = `/jobs/${jobId}/shortlist`;

    return useQuery({
        enabled: is.nonEmptyString(jobId),
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(jobShortListCandidateArraySchema, {
                pathname,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, 'GET'],
    });
};

type JobShortListMutations = {
    readonly addToShortListMutation: UseMutationResult<readonly JobShortListCandidate[]>;
    readonly removeFromShortListMutation: UseMutationResult<readonly JobShortListCandidate[]>;
    readonly reorderShortListMutation: UseMutationResult<readonly JobShortListCandidate[]>;
};

export const useJobShortListMutations = (jobId: string): JobShortListMutations => {
    const queryClient = useQueryClient();
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const { invalidateJobQuery } = useJobQuery(jobId);

    const pathname: Pathname = `/jobs/${jobId}/shortlist`;
    const reorderPathname: Pathname = `/jobs/${jobId}/shortlist/order`;

    const shortlistQueryKey: QueryKey = [pathname, 'GET'];

    const updateCandidateShortlistStatusCache = (candidateId: string, isOnShortList: boolean): void => {
        const candidateQueryKey = getCandidateQueryKey(jobId, candidateId);

        const candidate: User | undefined = queryClient.getQueryData(candidateQueryKey);

        if (is.plainObject(candidate)) {
            queryClient.setQueryData(candidateQueryKey, {
                ...candidate,
                isOnShortList,
            });
        }
    };

    const invalidateShortlistChange = async (): Promise<void> => {
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: [candidatesQueryKeyPrefix] }),
            queryClient.invalidateQueries({ queryKey: [pathname, 'GET'] }),
            invalidateJobQuery(),
        ]);
    };

    const addToShortListMutation = useMutation({
        mutationFn: async (data: readonly string[]) => {
            const result = await validatingCobrainerApiFetcher.patchJson(jobShortListCandidateArraySchema, {
                pathname,
                payload: data,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        onMutate: ([candidateId]) => updateCandidateShortlistStatusCache(candidateId ?? '', true),
        onSettled: invalidateShortlistChange,
    });

    const removeFromShortListMutation = useMutation({
        mutationFn: async (data: readonly string[]) => {
            const result = await validatingCobrainerApiFetcher.deleteJson(jobShortListCandidateArraySchema, {
                pathname,
                payload: data,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        onMutate: ([candidateId]) => updateCandidateShortlistStatusCache(candidateId ?? '', true),
        onSettled: invalidateShortlistChange,
    });

    const reorderShortListOptimistic = (newShortListOrder: readonly string[]): void => {
        queryClient.cancelQueries({ queryKey: shortlistQueryKey });

        const getOrderIndex = (candidateId: string): number => newShortListOrder.findIndex((id) => candidateId === id);
        const sortByIndex = (candidateA: JobCandidate, candidateB: JobCandidate): number =>
            getOrderIndex(candidateA.id) - getOrderIndex(candidateB.id);

        // Workaround for jumping CandidateShortlist on reorder
        const reorderShortList = (oldShortList: JobShortListCandidate[]): readonly JobShortListCandidate[] => {
            return oldShortList.sort(sortByIndex);
        };

        queryClient.setQueryData(shortlistQueryKey, reorderShortList);
    };

    const reorderShortListMutation = useMutation({
        mutationFn: async (data: readonly string[]) => {
            const result = await validatingCobrainerApiFetcher.postJson(jobShortListCandidateArraySchema, {
                pathname: reorderPathname,
                payload: data,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        onMutate: reorderShortListOptimistic,
        onSettled: () => queryClient.invalidateQueries({ queryKey: shortlistQueryKey }),
    });

    return {
        addToShortListMutation,
        removeFromShortListMutation,
        reorderShortListMutation,
    };
};

type ShortListExportMutation = UseMutationResult<ExportFile>;

export const useShortListExportMutation = (jobId: string): ShortListExportMutation => {
    const { applicationState, validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const { userIsAnonymized } = applicationState;

    const pathname: Pathname = `/jobs/${jobId}/shortlist/export`;

    return useMutation({
        mutationFn: async () => {
            const result = await validatingCobrainerApiFetcher.postJson(exportFileSchema, {
                pathname,
                payload: { anonymized: userIsAnonymized },
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
    });
};

export const useJobCountQuery = (jobState?: JobState | 'all'): UseQueryResult<PaginationCountResponse> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    const jobFilterQuery = useJobFilter(jobState);
    const path = '/jobs/filter';
    const paged = {
        countOnly: true,
    };

    return useQuery({
        enabled: jobFilterQuery.isSuccess,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.postJson(paginationCountResponseSchema, {
                pathname: path,
                payload: {
                    filter: jobFilterQuery.data?.filterPayload.unwrapOr(undefined),
                    paged,
                },
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },

        queryKey: [path, 'POST', paged, jobFilterQuery.data?.filterPayload],
    });
};

const path = '/jobs/generation';

export type JobDescriptionGenerationMutationResponse = {
    readonly cancelRequest: () => void;
    readonly mutation: UseMutationResult<Result<JobGenerationResponse, Error>, unknown, JobGenerateDescriptionRequest>;
};

export const useJobDescriptionGenerationMutation = (
    languageCode: LanguageCode,
): JobDescriptionGenerationMutationResponse => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    const abortControllerRef = useRef(new AbortController());

    const cancelRequest = (): void => {
        abortControllerRef.current.abort();
        abortControllerRef.current = new AbortController();
    };

    const mutation = useMutation<Result<JobGenerationResponse, Error>, unknown, JobGenerateDescriptionRequest>({
        mutationFn: (jobData) => {
            return validatingCobrainerApiFetcher.postJson(jobGenerationResponseSchema, {
                pathname: path,
                payload: jobData,
                requestOptions: {
                    headers: {
                        'Accept-Language': languageCode,
                    },
                    searchParams: {
                        clearCache: true,
                        maxLength: 1000,
                        minLength: 700,
                    },
                    signal: abortControllerRef.current.signal,
                    timeout: 30_000,
                },
            });
        },
        mutationKey: [path, 'POST', languageCode],
        onMutate: cancelRequest,
    });

    return {
        cancelRequest,
        mutation,
    };
};
